import {inject, Injectable, OnDestroy} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {BehaviorSubject, debounceTime, delay, Observable, of, Subject, switchMap, take, takeUntil, tap} from 'rxjs';
import {FormBuilder} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {TelescopeIcon} from "lucide-angular";
import {catchError} from 'rxjs/operators';
import {HttpStatusCode} from '@angular/common/http';
import {DayTime, PersonGender, WeekDay} from '@domain/common/common';
import {ActivityScheduleLevel} from '@domain/activity/schedule/schedule.dto';
import {ActivitySearchQuery, ActivitySearchResponse, ActivityType} from '@domain/activity/activity.dto';
import {ActivityPublicApi} from '@domain/activity/activity.public.api';
import {NotificationService} from '@shared/notification/notification.service';
import {AgeUtil} from '@domain/common/util/age.util';
import {OverlayService} from '@shared/overlay/overlay.service';

@Injectable({
  providedIn: 'root'
})
export class ActivitySearchService implements OnDestroy {

  public static readonly QUERY_TOWN_CODE = 'town';

  public readonly searchResponse$ = new BehaviorSubject<ActivitySearchResponse | null>(null);
  public readonly selectedDays$ = new BehaviorSubject<WeekDay[]>([]);
  public readonly selectedDayTimes$ = new BehaviorSubject<DayTime[]>([]);
  public readonly selectedLevels$ = new BehaviorSubject<ActivityScheduleLevel[]>([]);
  public readonly selectedGenders$ = new BehaviorSubject<PersonGender[]>([]);
  public readonly selectedActivityTypes$ = new BehaviorSubject<ActivityType[]>([]);
  public readonly selectedIsRecurring$ = new BehaviorSubject<number[]>([]);

  private readonly activityPublicApi = inject(ActivityPublicApi);
  private readonly overlayService = inject(OverlayService);
  private readonly notifyService = inject(NotificationService);
  private readonly transloco = inject(TranslocoService);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);

  private readonly weekDays = Object.values(WeekDay) as WeekDay[];
  private readonly levels = Object.values(ActivityScheduleLevel) as ActivityScheduleLevel[];
  private readonly genders = Object.values(PersonGender) as PersonGender[];
  private readonly activityTypes = Object.values(ActivityType) as ActivityType[];
  private readonly dayTimes = Object.values(DayTime) as DayTime[];
  private readonly isRecurringValues = [0, 1];

  private tooManyRequests: Date | null = null;
  private static readonly MSG_TOO_MANY_REQUESTS = 'Oups il y a eu beaucoup de requêtes, veuillez patienter quelques minutes.';

  private filterDefaults = {
    weekDays: {
      [WeekDay.MO]: false,
      [WeekDay.TU]: false,
      [WeekDay.WE]: false,
      [WeekDay.TH]: false,
      [WeekDay.FR]: false,
      [WeekDay.SA]: false,
      [WeekDay.SU]: false,
    },
    dayTimes: {
      [DayTime.Morning]: false,
      [DayTime.Noon]: false,
      [DayTime.Afternoon]: false,
      [DayTime.Evening]: false,
      [DayTime.Night]: false,
    },
    ageRange: {
      min: AgeUtil.MIN,
      max: AgeUtil.MAX,
    },
    levels: {
      [ActivityScheduleLevel.All]: false,
      [ActivityScheduleLevel.Beginner]: false,
      [ActivityScheduleLevel.Intermediate]: false,
      [ActivityScheduleLevel.Advanced]: false
    },
    genders: {
      [PersonGender.Mx]: false,
      [PersonGender.Ms]: false,
      [PersonGender.Mr]: false
    },
    activityTypes: {
      [ActivityType.Activity]: false,
      [ActivityType.Event]: false
    },
    recurring: {
      0: false,
      1: false
    },
  };
  public readonly filterForm = this.fb.group({
    weekDays: this.fb.group(this.filterDefaults.weekDays),
    dayTimes: this.fb.group(this.filterDefaults.dayTimes),
    ageRange: this.fb.group(this.filterDefaults.ageRange),
    levels: this.fb.group(this.filterDefaults.levels),
    genders: this.fb.group(this.filterDefaults.genders),
    activityTypes: this.fb.group(this.filterDefaults.activityTypes),
    recurring: this.fb.group(this.filterDefaults.recurring),
  });

  private readonly destroy$ = new Subject<void>();

  public constructor() {
    this.filterForm.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      switchMap(_ => this.search(false))
    ).subscribe(_ => {});
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public search(showOverlay: boolean): Observable<ActivitySearchResponse | null> {
    const query = this.getSearchQuery();
    if (!query) {
      return of(null);
    }
    if (this.tooManyRequests && Date.now() - this.tooManyRequests.getTime() < 60_000) {
      this.notifyService.toast({
        message: ActivitySearchService.MSG_TOO_MANY_REQUESTS
      });
      return of(null);
    }
    const searchDelay = showOverlay ? 1_000 : 300;
    if (showOverlay) {
      this.overlayService.show({
        level: 'info',
        message: this.transloco.translate('guest.search.searching'),
        icon: TelescopeIcon
      });
    }
    return this.activityPublicApi.search(query).pipe(
      take(1),
      delay(searchDelay),
      catchError(error => {
        if (error.status === HttpStatusCode.TooManyRequests) {
          this.tooManyRequests = new Date();
          this.notifyService.toast({
            message: ActivitySearchService.MSG_TOO_MANY_REQUESTS
          });
        }
        return of(null);
      }),
      tap(searchResponse => {
        if (searchResponse) {
          this.searchResponse$.next(searchResponse);
        }
        this.overlayService.hide();
      })
    );
  }

  public resetSearch(): void {
    this.searchResponse$.next(null);
  }

  public getSearchResponse(): ActivitySearchResponse | null {
    return this.searchResponse$.value;
  }

  public resetFilters(): void {
    this.selectedDays$.next([]);
    this.selectedDayTimes$.next([]);
    this.selectedLevels$.next([]);
    this.selectedGenders$.next([]);
    this.selectedActivityTypes$.next([]);
    this.selectedIsRecurring$.next([]);
    this.filterForm.reset(this.filterDefaults);
  }

  public toggleDay(day: string) {
    const control = this.filterForm.controls.weekDays.get(day)!;
    control.setValue(!control.value);
    this.selectedDays$.next(this.getEnabledWeekDays());
  }

  public toggleLevel(level: string) {
    const control = this.filterForm.controls.levels.get(level)!;
    control.setValue(!control.value);
    this.selectedLevels$.next(this.getEnabledLevels());
  }

  public toggleGender(gender: string) {
    const control = this.filterForm.controls.genders.get(gender)!;
    control.setValue(!control.value);
    this.selectedGenders$.next(this.getEnabledGenders());
  }

  public toggleActivityType(activityType: string) {
    const control = this.filterForm.controls.activityTypes.get(activityType)!;
    control.setValue(!control.value);
    this.selectedActivityTypes$.next(this.getEnabledActivityTypes());
  }

  public toggleIsRecurring(isRecurring: number) {
    const control = this.filterForm.controls.recurring.get(isRecurring.toString())!;
    control.setValue(!control.value);
    this.selectedIsRecurring$.next(this.getEnabledIsRecurring());
  }

  public toggleDayTime(time: string) {
    const control = this.filterForm.controls.dayTimes.get(time)!;
    control.setValue(!control.value);
    this.selectedDayTimes$.next(this.getEnabledDayTimes());
  }

  public getSearchQuery(): ActivitySearchQuery | null {
    const searchResponse = this.getSearchResponse();
    const townCode: string | undefined = searchResponse?.town?.code
      ?? this.route.snapshot.queryParams[ActivitySearchService.QUERY_TOWN_CODE];
    if (!townCode) {
      return null;
    }

    const query: ActivitySearchQuery = {townCode: townCode};
    const dayStates = this.filterForm.controls.weekDays.value;
    const enabledDays = this.weekDays.filter(day => !!dayStates[day]);
    if (enabledDays.length > 0 && enabledDays.length < this.weekDays.length) {
      query.daysOfWeek = enabledDays;
    }

    const enabledTimes = this.getEnabledDayTimes();
    if (enabledTimes.length > 0 && enabledTimes.length < this.dayTimes.length) {
      query.dayTimes = enabledTimes;
    }

    const enabledIsRecurring = this.getEnabledIsRecurring();
    if (enabledIsRecurring.length > 0 && enabledIsRecurring.length < this.isRecurringValues.length) {
      query.recurring = !!enabledIsRecurring[0];
    }

    const enabledLevels = this.getEnabledLevels();
    if (enabledLevels.length > 0 && enabledLevels.length < this.levels.length) {
      query.level = enabledLevels;
    }

    const enabledGenders = this.getEnabledGenders();
    if (enabledGenders.length > 0 && enabledGenders.length < this.genders.length) {
      query.gender = enabledGenders;
    }

    const enabledActivityTypes = this.getEnabledActivityTypes();
    if (enabledActivityTypes.length > 0 && enabledActivityTypes.length < this.activityTypes.length) {
      query.activityType = enabledActivityTypes;
    }

    const ageRange = this.filterForm.controls.ageRange.value;
    if (ageRange?.min && ageRange.min !== AgeUtil.MIN) {
      query.ageMin = ageRange.min;
    }
    if (ageRange?.max && ageRange.max !== AgeUtil.MAX) {
      query.ageMax = ageRange.max;
    }
    return query;
  }

  private getEnabledWeekDays(): WeekDay[] {
    const weekDays = this.filterForm.controls.weekDays.value as {[key: string]: boolean;};
    return this.weekDays.filter(day => !!weekDays?.[day]);
  }

  private getEnabledDayTimes(): DayTime[] {
    const timeStates = this.filterForm.controls.dayTimes.value;
    return this.dayTimes.filter(time => !!timeStates[time]);
  }

  private getEnabledLevels(): ActivityScheduleLevel[] {
    const levels = this.filterForm.controls.levels.value as {[key: string]: boolean;};
    return this.levels.filter(level => !!levels?.[level]);
  }

  private getEnabledGenders(): PersonGender[] {
    const genders = this.filterForm.controls.genders.value as {[key: string]: boolean;};
    return this.genders.filter(gender => !!genders?.[gender]);
  }

  private getEnabledActivityTypes(): ActivityType[] {
    const types = this.filterForm.controls.activityTypes.value as {[key: string]: boolean;};
    return this.activityTypes.filter(activityType => !!types?.[activityType]);
  }

  private getEnabledIsRecurring(): number[] {
    const recurringValues = this.filterForm.controls.recurring.value as {[key: number]: boolean;};
    return this.isRecurringValues.filter(val => !!recurringValues?.[val]);
  }

}
